import { createContext, useContext, useCallback } from 'react';

/**
 * PortalContext - provides portal root element for dropdown components
 *
 * When widget is mounted in Shadow DOM, dropdowns need to render inside
 * the Shadow DOM to inherit styles. This context provides the portal root
 * element that lives inside Shadow DOM.
 *
 * Usage:
 * ```jsx
 * // In widget.jsx or App.jsx
 * <PortalProvider value={portalRootElement}>
 *   <App />
 * </PortalProvider>
 *
 * // In dropdown components
 * const portalRoot = usePortalRoot();
 * createPortal(<Dropdown />, portalRoot);
 * ```
 */
export const PortalContext = createContext(null);

/**
 * Hook to get portal root element for createPortal
 * Falls back to document.body if no context is provided (standalone mode)
 *
 * @returns {HTMLElement} Portal root element
 */
export function usePortalRoot() {
  const portalRoot = useContext(PortalContext);
  // Fallback to document.body for standalone mode (not in Shadow DOM)
  return portalRoot || document.body;
}

/**
 * Provider component for portal root
 */
export const PortalProvider = PortalContext.Provider;

/**
 * ModalPortalContext — a SECOND portal target, this one at `document.body`
 * level (its own shadow host), NOT inside the component's embed shadow root.
 *
 * Why (#1232): the form modal previously rendered inline in the embed's shadow
 * root. On real customer pages that shadow host is often trapped inside a low
 * `z-index` stacking context (e.g. a host `.navbar { position:fixed; z-index:999 }`
 * wins over the embed even at `z-index:2147483000`), and the host's fixed
 * `inset:0` chrome makes the modal backdrop resolve to `height:auto → 0`. Both
 * break the mobile modal (invisible sheet, unreachable × close). Rendering the
 * modal into a body-level node fixes both: its `position:fixed` escapes to the
 * real viewport and its `z-index` competes at the top layer.
 *
 * The provider value is a THUNK that lazily creates (once) the body-level shadow
 * host. `useModalPortalRoot()` returns a stable RESOLVER; the caller invokes it
 * only when actually opening a modal, so pages with inline-only forms never
 * create the body node.
 */
export const ModalPortalContext = createContext(null);

/**
 * Hook returning a stable resolver for the body-level modal portal root. Call it
 * at modal-open time: it returns the (lazily-created, idempotent) root, or
 * `null` when no provider is present (tests / standalone) so the caller renders
 * the modal inline in place — the body-level portal is a widget-only concern
 * (escaping a host's trapped stacking context).
 *
 * @returns {() => HTMLElement|null} resolver → portal root, or null to render inline
 */
export function useModalPortalRoot() {
  const getterOrNode = useContext(ModalPortalContext);
  return useCallback(() => {
    if (typeof getterOrNode === 'function') return getterOrNode();
    return getterOrNode || null;
  }, [getterOrNode]);
}

/**
 * Provider component for the body-level modal portal root
 */
export const ModalPortalProvider = ModalPortalContext.Provider;

export default PortalContext;
